import React, { useState } from "react";
import executiveProjectsData from "../data/executiveProjectsData";
import ProjectModal from "./ProjectModal";

const ExecutiveShowcase = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeModalProject, setActiveModalProject] = useState(null);

  const categories = ["All", ...Array.from(new Set(executiveProjectsData.map((project) => project.category)))];

  const filteredProjects =
    selectedCategory === "All"
      ? executiveProjectsData
      : executiveProjectsData.filter((project) => project.category === selectedCategory);

  return (
    <section id="enterprise-track-record" className="pt-32 pb-28 bg-slate-950 text-slate-100 relative overflow-hidden bg-cyber-grid">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="mx-auto mb-16 max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-xs font-bold uppercase tracking-widest mb-4">
            🚀 ENTERPRISE SOFTWARE TRACK RECORD
          </div>
          <h2 className="mb-4 text-3xl font-black text-white sm:text-4xl lg:text-5xl tracking-tight">
            Full-Stack Software &amp; <span className="text-emerald-400">Microservices Architecture</span>
          </h2>
          <p className="text-base font-medium text-slate-400 md:text-lg leading-relaxed">
            Delivering high-throughput enterprise web applications, Java Quarkus microservices, and BUMN Super-Apps across Indonesia&apos;s leading tech ecosystems.
          </p>
        </div>

        {/* Master Deck V5 Download Hub Banner */}
        <div className="mb-16 mx-auto max-w-5xl rounded-3xl bg-slate-900/90 border border-emerald-500/40 p-8 md:p-10 text-white shadow-2xl backdrop-blur-md relative overflow-hidden animate-pulse-emerald">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 relative z-10">
            <div className="max-w-2xl">
              <span className="inline-block rounded-full bg-emerald-500/20 border border-emerald-400/40 px-3.5 py-1 text-xs font-mono font-bold uppercase tracking-wider text-emerald-400 mb-3">
                ⭐ Official Master Deck V5 (Senior Full-Stack Software Engineer Edition)
              </span>
              <h3 className="text-2xl font-black sm:text-3xl tracking-tight mb-3 font-mono">
                Project Development Portfolio V5 (`Master Deck`)
              </h3>
              <p className="text-sm md:text-base font-normal text-slate-300 leading-relaxed">
                Featuring our mathematical <strong className="text-emerald-400">True Widescreen (13.333 x 7.50 in / 960 x 540 pt)</strong> geometry. Comprehensive breakdowns of IFG Life Corporate LifeHub (`Quarkus + BPMN`), PT Bukit Asam Tbk CISEA v2.0.0 Super-App (100+ modules), and Investree Fintech portals.
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 shrink-0">
              <a
                href={`${process.env.PUBLIC_URL}/documents/Project_Development_Portfolio_Haidar_v5_Master.pdf`}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-6 py-4 font-mono font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:scale-105"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Download PDF (`720 KB`)</span>
              </a>

              <a
                href={`${process.env.PUBLIC_URL}/documents/Project_Development_Portfolio_Haidar_v5_Master.pptx`}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 rounded-2xl border border-indigo-500/40 bg-slate-950 hover:bg-slate-900 px-6 py-4 font-mono font-bold text-xs uppercase text-indigo-400 tracking-wider transition-all duration-300 hover:scale-105"
              >
                <svg className="h-5 w-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>PPTX Editable (`825 KB`)</span>
              </a>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-12 flex flex-wrap justify-center gap-3">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`rounded-2xl px-6 py-3 font-mono text-xs font-bold transition-all duration-300 ${
                selectedCategory === cat
                  ? "bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/30 scale-105"
                  : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Project Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="cyber-card group rounded-3xl p-6 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold uppercase">
                    {project.category}
                  </span>
                  <span className="text-xs font-mono text-slate-500">{project.period || "2021 - 2026"}</span>
                </div>

                <h3 className="text-xl font-extrabold text-white mb-2 group-hover:text-emerald-400 transition-colors font-mono">
                  {project.title}
                </h3>

                {project.role && (
                  <div className="text-[11px] font-mono text-emerald-400/90 font-semibold mb-2">
                    ⚡ {project.role}
                  </div>
                )}

                <p className="text-xs text-slate-300 leading-relaxed mb-6 font-normal">
                  {project.subtitle || project.summary || project.shortDesc || project.description}
                </p>

                {/* Tech Badges */}
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {(project.techStack || project.tags || ["TypeScript", "Next.js", "Node.js"]).map((tech, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md bg-slate-950 border border-slate-800 text-slate-300 font-mono text-[10px]">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveModalProject(project)}
                className="w-full py-3 rounded-xl bg-slate-950 hover:bg-emerald-500 hover:text-slate-950 border border-emerald-500/30 font-mono font-bold text-xs uppercase tracking-wider text-emerald-400 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <span>🔍 Project Breakdown &amp; Technical Overview</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Modal */}
      {activeModalProject && (
        <ProjectModal
          project={activeModalProject}
          onClose={() => setActiveModalProject(null)}
        />
      )}
    </section>
  );
};

export default ExecutiveShowcase;
